import * as React from 'react';
import { set } from 'lodash-es';
import { IVector3 } from '@easytwin/runtime';
import { toScaledVec3 } from '@/utils';
import { ProInputNumber, ProInputNumberProps } from './ProInputNumber';
import { ImmerFormItem } from './ImmerFormItem';
import { FormItemsInRow } from './FormItemsInRow';
import { LockIcon } from './LockIcon';
import { ProInputEulerNumber } from './ProInputEulerNumber';

interface Vector3FormItemProps
  extends Omit<
    ProInputNumberProps,
    'onChange' | 'value' | 'onBlur' | 'prefix' | 'onPointMoveStart' | 'onPointMoveEnd'
  > {
  label: string;
  value: IVector3;
  useEuler?: boolean;
  prefix?: [string, string, string];
  constrainable?: boolean;
  onChange?: (name: string, value: IVector3 | undefined) => void;
  onBlur: (name: string, value: IVector3 | undefined) => void;
  tooltip?: React.ReactNode;
  hideY?: boolean;
  hideZ?: boolean;
  hideX?: boolean;
}

export const Vector3FormItem: React.FC<Vector3FormItemProps> = (props) => {
  const {
    label,
    value,
    useEuler,
    constrainable,
    name,
    prefix,
    onBlur,
    onChange,
    tooltip,
    hideY,
    hideZ,
    hideX,
    disabled,

    ...rest
  } = props;

  const [isScaled, setIsScaled] = React.useState(false);
  const originVector3Ref = React.useRef<IVector3 | undefined>(undefined);

  const handleChange = (n: string, v: number) => {
    const value = originVector3Ref.current;
    if (!value) return;
    const newValue = { ...value };
    set(newValue, n, v);
    const scaledValue = isScaled ? toScaledVec3(newValue, value) : newValue;
    onChange?.(name, scaledValue);
  };

  const handleBlur = (n: string, v: number) => {
    const value = originVector3Ref.current;
    if (!value) return;
    const newValue = { ...value };
    set(newValue, n, v);
    const scaledValue = isScaled ? toScaledVec3(newValue, value) : newValue;
    onBlur(name, scaledValue);
  };

  const BaseInput = useEuler ? ProInputEulerNumber : ProInputNumber;

  const handleStartInput = () => {
    originVector3Ref.current = value;
  };

  const handleEndInput = (n: string, v: number) => {
    handleBlur(n, v);
    originVector3Ref.current = undefined;
  };

  const visibleChildren = [
    !hideX && (
      <BaseInput
        key='x'
        name='x'
        value={value.x}
        prefix={prefix?.[0] || 'X'}
        disabled={disabled}
        {...rest}
        onPointMoveStart={handleStartInput}
        onPointMoveEnd={handleEndInput}
        onFocus={handleStartInput}
        onChange={handleChange}
        onBlur={handleEndInput}
      />
    ),
    !hideY && (
      <BaseInput
        key='y'
        name='y'
        value={value.y}
        prefix={prefix?.[1] || 'Y'}
        disabled={disabled}
        {...rest}
        onPointMoveStart={handleStartInput}
        onPointMoveEnd={handleEndInput}
        onFocus={handleStartInput}
        onChange={handleChange}
        onBlur={handleEndInput}
      />
    ),
    !hideZ && (
      <BaseInput
        key='z'
        name='z'
        value={value.z}
        prefix={prefix?.[2] || 'Z'}
        disabled={disabled}
        {...rest}
        onPointMoveStart={handleStartInput}
        onPointMoveEnd={handleEndInput}
        onFocus={handleStartInput}
        onChange={handleChange}
        onBlur={handleEndInput}
      />
    ),
  ].filter(Boolean);

  return (
    <ImmerFormItem
      disabled={disabled}
      label={label}
      nodeAfter={
        constrainable && (
          <LockIcon disabled={disabled} value={isScaled} onChange={setIsScaled} size={14} />
        )
      }
      tooltip={tooltip}
    >
      <FormItemsInRow>{visibleChildren}</FormItemsInRow>
    </ImmerFormItem>
  );
};
